/**
 * Contents nav for a school page — `md` and up only.
 *
 * What: A row of in-page anchor chips rendered directly under the hero, one per
 * anchored section, ending with a tinted jump to the tuition band.
 * Why: A school page carries ~870 words of school prose plus ten school FAQs.
 * The nav states that depth up front and gives the minority who arrived with
 * tuition intent a direct jump instead of a scroll.
 *
 * Desktop-only, deliberately. On a 390px phone the eight chips need three
 * wrapped rows (~160px, directly above the cut-off table most of this page's
 * traffic came to read) or one horizontally scrolling row, and a scroll row
 * hides its own contents behind a gesture nobody asks for. Neither is worth it,
 * because on mobile both of the nav's jobs are already covered: the section
 * order runs school content unbroken to ~10,000px so there is no branded band
 * to mistake for the end of the page, and the hero's trial CTA sits above the
 * fold at ~600px for anyone with buying intent. On desktop the row costs
 * horizontal space that was empty anyway.
 * When: Update when the school page gains or loses an anchored section.
 *
 * Hydration note: `/schools/*` is prerendered, so nothing here may branch on
 * viewport in JS — the breakpoint resolves in a CSS media query, and the
 * anchors stay in the markup at every width so crawlers still see them.
 */
import React from 'react';
import { Box } from '@mui/material';

import type { SchoolRichHeritage } from '@shared/schools/schoolRichHeritage.js';
import { colors, radii } from '@/components/landing/landingTokens';
import { SCHOOL_ABOUT_SECTION_ID } from '@/components/schools/SchoolAboutSection';
import { SCHOOL_CUT_OFF_SECTION_ID } from '@/components/schools/SchoolCutOffSection';
import {
  SCHOOL_HERITAGE_FAQ_ID,
  schoolHeritageSectionId,
} from '@/components/schools/SchoolRichContent';
import { SCHOOL_SUPPORT_SECTION_ID } from '@/components/schools/SchoolSupportSection';

export interface SchoolNavItem {
  label: string;
  targetId: string;
  /** The tuition band — tinted apart so it doesn't read as another school section. */
  isTuition?: boolean;
}

/**
 * Below this the nav is noise: a school with no heritage data has nothing to
 * advertise, and a two-chip list under a hero looks like a broken component.
 */
const MIN_NAV_ITEMS = 4;

/**
 * Whether `SchoolPageNav` will render anything for these items.
 *
 * Exported so the hero can close its bottom padding against the nav at the
 * widths that show one, and against the profile card everywhere else.
 */
export function hasSchoolPageNav(items: SchoolNavItem[]): boolean {
  return items.length >= MIN_NAV_ITEMS;
}

/**
 * The nav's responsive `display`, exported because it is the whole decision.
 *
 * A breakpoint expressed in emotion's generated CSS is not readable back from
 * jsdom's computed style, so the contract "never on a phone" is pinned here
 * rather than in a render assertion that can only pass vacuously.
 */
export const SCHOOL_NAV_DISPLAY = { xs: 'none', md: 'flex' } as const;

/**
 * Short nav labels for the heritage headings.
 *
 * The heritage set is near-standardised — thirteen distinct headings across the
 * 349 schools — but it is authored data, not an enum, so an unmatched heading
 * falls through to itself rather than being dropped. `facilities` is tested
 * before `curriculum` because "Co-Curricular Activities" lives in the
 * facilities heading on almost every school.
 */
const HERITAGE_NAV_LABELS: ReadonlyArray<readonly [RegExp, string]> = [
  [/overview/i, 'Overview'],
  [/facilities|campus/i, 'Facilities'],
  [/admission/i, 'Admissions'],
  [/student life|culture|community/i, 'School life'],
  [/curriculum|academic/i, 'Curriculum'],
];

function heritageNavLabel(heading: string): string {
  for (const [pattern, label] of HERITAGE_NAV_LABELS) {
    if (pattern.test(heading)) return label;
  }
  return heading;
}

interface BuildSchoolNavItemsArgs {
  hasCutOff: boolean;
  hasAbout: boolean;
  richHeritage: SchoolRichHeritage | null;
}

/**
 * Derive the nav from the sections the page actually renders.
 *
 * Kept as a pure function so the ordering contract — school content first, the
 * tuition jump last — is testable without mounting the page.
 */
export function buildSchoolNavItems({
  hasCutOff,
  hasAbout,
  richHeritage,
}: BuildSchoolNavItemsArgs): SchoolNavItem[] {
  const items: SchoolNavItem[] = [];

  if (hasCutOff) {
    items.push({
      label: 'Cut-off points',
      targetId: SCHOOL_CUT_OFF_SECTION_ID,
    });
  }
  if (hasAbout) {
    items.push({ label: 'About', targetId: SCHOOL_ABOUT_SECTION_ID });
  }

  (richHeritage?.sections ?? []).forEach((section, idx) => {
    const label = heritageNavLabel(section.heading);
    // The About card and the heritage overview prose are the same subject a
    // screen apart. Two chips for it spends the reader's attention on a
    // distinction they can't act on, and About sits higher, so About wins.
    if (hasAbout && label === 'Overview') return;
    items.push({ label, targetId: schoolHeritageSectionId(idx) });
  });

  if (richHeritage && richHeritage.faqs.length > 0) {
    items.push({ label: 'FAQs', targetId: SCHOOL_HERITAGE_FAQ_ID });
  }

  items.push({
    label: 'Tuition',
    targetId: SCHOOL_SUPPORT_SECTION_ID,
    isTuition: true,
  });

  return items;
}

interface SchoolPageNavProps {
  items: SchoolNavItem[];
}

function SchoolPageNav({ items }: SchoolPageNavProps) {
  if (!hasSchoolPageNav(items)) return null;

  return (
    <Box
      component="nav"
      aria-label="On this page"
      sx={{
        // Hidden rather than unrendered: the anchors stay crawlable at every
        // width, and a CSS-only breakpoint keeps the prerendered markup and the
        // hydrated markup identical.
        display: SCHOOL_NAV_DISPLAY,
        flexWrap: 'wrap',
        gap: 1,
        // The gap the hero used to open to the profile card, now opened here.
        mb: 6,
      }}
    >
      {items.map(item => (
        <Box
          key={item.targetId}
          component="a"
          href={`#${item.targetId}`}
          sx={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: 48,
            px: 2,
            borderRadius: radii.pill,
            border: `1px solid ${item.isTuition ? colors.accentCobaltLight : colors.border}`,
            backgroundColor: item.isTuition ? colors.washCobalt : colors.paper,
            color: item.isTuition ? colors.accentCobaltDark : colors.ink,
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            transition:
              'background-color 0.2s var(--ease-default), border-color 0.2s var(--ease-default)',
            '&:hover': {
              backgroundColor: colors.washCobalt,
              borderColor: colors.accentCobaltLight,
            },
            '&:focus-visible': {
              outline: `3px solid ${colors.accentCobalt}`,
              outlineOffset: 2,
            },
          }}
        >
          {item.label}
        </Box>
      ))}
    </Box>
  );
}

export default SchoolPageNav;
